import { useDispatch } from 'react-redux';
import axios from '../utils/axiosConfig';
import { errorHandler } from '../utils/errorHandler';
import {
    setUsersLoading,
    setUsersError,
    setUsers,
    setImportUsersLoading,
    setImportUsersError,
    setImportUsersSuccess
} from '../redux/slices/manageUsers';
import {
    fetchAzureTeamsStart,
    fetchAzureTeamsSuccess,
    fetchAzureTeamsFailure,
    importAzureTeamsStart,
    importAzureTeamsSuccess,
    importAzureTeamsFailure,
    fetchAzureSyncDifferencesStart,
    fetchAzureSyncDifferencesSuccess,
    fetchAzureSyncDifferencesFailure,
    azureSyncActionsStart,
    azureSyncActionsSuccess,
    azureSyncActionsFailure
} from '../redux/slices/azureTeams';
import { User, UsersResponse, ImportUsersResponse } from '../types/manageUsers';
import { AzureTeamsResponse, AzureTeam, AzureTeamsImportResponse } from '../types/azureTeams';
import azureTeamSyncRepository, { AzureTeamSyncData } from './repositories/azureTeamSyncRepository';
import toast from "react-hot-toast";

interface ProjectOption {
    value: string;
    label: string;
}

interface EpicOption {
    value: string;
    label: string;
    isDisabled?: boolean;
    isImported?: boolean;
    epic_id?: number;
}

// Define interfaces for the nested status stages structure
interface StatusStageItem {
    [key: string]: string;
}

interface StatusStageTeam {
    [stateName: string]: StatusStageItem;
}

interface StatusStageProject {
    [teamName: string]: StatusStageTeam;
}

interface StatusStages {
    [projectName: string]: StatusStageProject;
}

// Add interface for status stages order
interface StatusStagesOrder {
    [projectName: string]: {
        [stateName: string]: string | number;
    };
}

interface ProjectsResponse {
    message: string;
    projects: Array<{
        project_name: string;
        project_id: string;
    }>;
}

interface EpicsResponse {
    message: string;
    epics: Array<{
        epic_name: string;
        epic_id: number | string;
        is_imported?: boolean;
    }>;
    status_stages?: StatusStages;
    status_stages_order?: StatusStagesOrder;
    display_message?: string;
    processing_status_stages?: boolean;
}

interface ImportEpicResponse {
    message: string;
    epic_id: string;
    imported_items_count: number;
}

// Add this interface for the update stages request
interface UpdateStagesRequest {
    status_stages: Record<string, Record<string, Record<string, string>>>;
}

interface UpdateStagesResponse {
    message: string;
}

// Add this interface for the Azure settings response
interface AzureSettingsResponse {
    organization?: string;
    api_token?: string;
    project_name?: string;
    is_active?: boolean;
    status_stages?: string | Record<string, any>;
    status_stages_order?: string | Record<string, any>;
    processing_states?: boolean;
    processed_projects?: Record<string, { hidden: boolean }>;
    priorities?: Record<string, any>;
}

// Interface for manage projects API
interface ManageProjectsRequest {
    projects: Array<{
        name: string;
        hidden: boolean;
    }>;
}

interface ManageProjectsResponse {
    message: string;
}

// Add interfaces for sync teams APIs
interface AzureTeamSyncMember {
    id: string;
    name: string;
    email: string;
}

interface AzureTeamSyncDifference {
    id: number;
    name: string;
    members: AzureTeamSyncMember[];
}

interface AzureSyncTeamsResponse {
    teams: AzureTeamSyncDifference[];
}

interface AzureAcceptRejectMembersRequest {
    team_id: number;
    members: AzureTeamSyncMember[];
}

interface AzureAcceptRejectMembersResponse {
    message: string;
    team_id: number;
}



const useAzureService = () => {
    const dispatch = useDispatch();

    const showError = (error: any) => {
        errorHandler.showError(error);
    };

    const isAzureAuthError = (error: any): boolean => {
        const detail = error?.response?.data?.detail || '';
        const message = error?.response?.data?.message || '';
        
        return (typeof detail === 'string' && (
                (detail.includes('401') && detail.includes('Unauthorized')) ||
                (detail.includes('403') && detail.includes('Forbidden')) ||
                detail.includes('TF400813') || 
                detail.toLowerCase().includes('token expired') ||
                detail.toLowerCase().includes('invalid token')
            )) || 
            (typeof message === 'string' && (
                (message.includes('401') && message.includes('Unauthorized')) ||
                (message.includes('403') && message.includes('Forbidden')) ||
                message.includes('TF400813') ||
                message.toLowerCase().includes('token expired') ||
                message.toLowerCase().includes('invalid token')
            ));
    };

    const handleAuthError = (error: any, defaultMessage: string, errorAction?: any) => {
        if (isAzureAuthError(error)) {
            const msg = "Please check your Azure DevOps credentials. Your token may have expired or is invalid.";
            toast.error(msg);
            if (errorAction) {
                dispatch(errorAction(msg));
            }
        } else {
            showError(error);
            if (errorAction) {
                dispatch(errorAction(error?.response?.data?.message || defaultMessage));
            }
        }
    };

    const fetchAzureProjects = async (): Promise<ProjectOption[]> => {
        try {
            const response = await axios.get<ProjectsResponse>('/integration/azure/projects');

            if (response.data && response.data.projects) {
                return response.data.projects.map(project => ({
                    value: project.project_id,
                    label: project.project_name
                }));
            }
            return [];
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    const fetchAzureEpics = async (projectName: string, projectId: string): Promise<{ epics: EpicOption[], statusStages?: StatusStages, statusStagesOrder?: StatusStagesOrder, displayMessage?: string }> => {
        try {
            const response = await axios.post<EpicsResponse>(`/integration/azure/epics`, {
                project_name: projectName,
                project_id: projectId
            });

            let epics: EpicOption[] = [];
            if (response.data && response.data.epics) {
                epics = response.data.epics.map(epic => ({
                value: String(epic.epic_id),
                label: epic.epic_name,
                isImported: (epic as any).is_imported || false,
                isDisabled: (epic as any).is_imported || false,
                epic_id: typeof epic.epic_id === 'number' ? epic.epic_id : parseInt(String(epic.epic_id), 10)
            }));
            }

            return {
                epics,
                statusStages: response.data.status_stages,
                statusStagesOrder: response.data.status_stages_order,
                displayMessage: response.data.display_message
            };
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    const importAzureEpic = async (epicIds: string[], projectName: string, statusStages?: any): Promise<ImportEpicResponse> => {
        try {
            // Always include status_stages, even if it's an empty object
            const payload: any = {
                epic_ids: epicIds,
                project_name: projectName,
                status_stages: statusStages || {}
            };

            const response = await axios.post<ImportEpicResponse>('/integration/azure/import_epic', payload);
            toast.success(response.data.message || 'Import successful');
            return response.data;
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    // Add this function to the useAzureService hook
    const updateAzureStages = async (statusStages: Record<string, Record<string, Record<string, string>>>): Promise<UpdateStagesResponse> => {
        try {
            const payload: UpdateStagesRequest = {
                status_stages: statusStages
            };

            const response = await axios.post<UpdateStagesResponse>('/integration/azure/update_stages', payload);
            toast.success(response.data.message || 'Status stages updated successfully');
            return response.data;
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    // Add this function to the useAzureService hook
    const fetchAzureSettings = async (existingData?: any): Promise<{ parsedStages: Record<string, any>, parsedStagesOrder?: Record<string, any>, processedProjects?: Record<string, { hidden: boolean }>, priorities?: Record<string, any> }> => {
        try {
            const responseData = existingData || (await axios.get<AzureSettingsResponse>('/integration/azure_settings')).data;

            let parsedStages = {};
            let parsedStagesOrder = {};
            let processedProjects: Record<string, { hidden: boolean }> = {};

            if (responseData && responseData.status_stages) {
                // Check if it's a string (for backward compatibility)
                if (typeof responseData.status_stages === 'string') {
                    console.error('Received status_stages as a string, but expected an object');
                    try {
                        // Try to parse it anyway
                        parsedStages = JSON.parse(responseData.status_stages);
                    } catch (error) {
                        console.error('Error parsing status_stages string:', error);
                        parsedStages = {};
                    }
                } else {
                    parsedStages = responseData.status_stages;
                }
            }

            // Handle status_stages_order
            if (responseData && responseData.status_stages_order) {
                if (typeof responseData.status_stages_order === 'string') {
                    try {
                        parsedStagesOrder = JSON.parse(responseData.status_stages_order);
                    } catch (error) {
                        console.error('Error parsing status_stages_order string:', error);
                        parsedStagesOrder = {};
                    }
                } else {
                    parsedStagesOrder = responseData.status_stages_order;
                }
            }

            // Handle processed_projects
            if (responseData && responseData.processed_projects) {
                processedProjects = responseData.processed_projects;
            }

            return { parsedStages, parsedStagesOrder, processedProjects, priorities: responseData?.priorities };
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    // Manage Azure projects visibility (hide/show)
    const manageAzureProjects = async (projects: Array<{ name: string; hidden: boolean }>): Promise<ManageProjectsResponse> => {
        try {
            const payload: ManageProjectsRequest = {
                projects
            };

            const response = await axios.put<ManageProjectsResponse>('/integration/azure/manage_projects', payload);
            toast.success(response.data.message || 'Projects updated successfully');
            return response.data;
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    // Add this function to the useAzureService hook
    const refreshAzureStatusStages = async (check_status_stages: boolean = false, suppressToast: boolean = false): Promise<{ epics: EpicOption[], statusStages?: StatusStages, statusStagesOrder?: StatusStagesOrder, displayMessage?: string, processing_status_stages?: boolean }> => {
        try {
            const response = await axios.post<EpicsResponse>(`/integration/azure/refresh_status_stages?check_status_stages=${check_status_stages}`);

            let epics: EpicOption[] = [];
            if (response.data && response.data.epics) {
                epics = response.data.epics.map(epic => ({
                    value: String(epic.epic_id),
                    label: epic.epic_name
                }));
            }

            if (!suppressToast) {
                toast.success(response.data.display_message || response.data.message || 'Status stages refreshed successfully');
            }

            return {
                epics,
                statusStages: response.data.status_stages,
                statusStagesOrder: response.data.status_stages_order,
                displayMessage: response.data.display_message,
                processing_status_stages: response.data.processing_status_stages
            };
        } catch (error) {
            showError(error);
            throw error;
        }
    };

    const fetchAzureUsers = async (): Promise<void> => {
        dispatch(setUsersLoading(true));
        dispatch(setUsersError(null));
        try {
            const response = await axios.get<UsersResponse>('/integration/azure/users');
            dispatch(setUsers({
                users: response.data.users,
                summary: response.data.summary
            }));
        } catch (error: any) {
            handleAuthError(error, 'Failed to fetch users', setUsersError);
        } finally {
            dispatch(setUsersLoading(false));
        }
    };

    const importAzureUsers = async (users: User[]): Promise<void> => {
        dispatch(setImportUsersLoading(true));
        dispatch(setImportUsersError(null));
        dispatch(setImportUsersSuccess(false));
        try {
            await axios.post<ImportUsersResponse>('/integration/azure/users', { users });
            toast.success("Azure users imported successfully.");
            dispatch(setImportUsersSuccess(true));
        } catch (error: any) {
            showError(error);
            dispatch(setImportUsersError(error?.response?.data?.message || 'Failed to import users'));
        } finally {
            dispatch(setImportUsersLoading(false));
        }
    };

    const fetchAzureTeams = async (): Promise<void> => {
        dispatch(fetchAzureTeamsStart());
        try {
            // Set custom timeout to 5 minutes (300000ms) for this specific API call
            const response = await axios.get<AzureTeamsResponse>('/integration/azure/teams', {
                timeout: 300000 // 5 minutes
            });
            dispatch(fetchAzureTeamsSuccess(response.data));
        } catch (error: any) {
            handleAuthError(error, 'Failed to fetch Azure teams', fetchAzureTeamsFailure);
        }
    };

    const fetchAzureTeamsSilently = async (): Promise<void> => {
        try {
            // Set custom timeout to 5 minutes (300000ms) for this specific API call
            const response = await axios.get<AzureTeamsResponse>('/integration/azure/teams', {
                timeout: 300000 // 5 minutes
            });
            dispatch(fetchAzureTeamsSuccess(response.data));
        } catch (error: any) {
            // Silent fetch - don't show error to user, just log it
            console.error('Silent fetch failed:', error);
            const msg = isAzureAuthError(error)
                ? "Please check your Azure DevOps credentials. Your token may have expired or is invalid." 
                : (error?.response?.data?.message || 'Failed to fetch Azure teams');
            dispatch(fetchAzureTeamsFailure(msg));
        }
    };

    const importAzureTeams = async (teams: AzureTeam[]): Promise<void> => {
        dispatch(importAzureTeamsStart());
        try {
            const payload = {
                teams: teams.map(team => ({
                    team_id: team.team_id,
                    team_name: team.team_name,
                    project_name: team.project_name,
                    project_id: team.project_id,
                    members: team.members.map(member => ({
                        id: member.id,
                        display_name: member.display_name,
                        email: member.email,
                        present_in_org: member.present_in_org,
                        selected: member.selected,
                        is_deleted: member.is_deleted
                    })),
                    selected: team.selected
                }))
            };

            await axios.post<AzureTeamsImportResponse>('/integration/azure/teams', payload);
            toast.success("Teams & Members successfully synced with Cadence Pro.");
            dispatch(importAzureTeamsSuccess({ message: "Teams imported successfully" }));
        } catch (error: any) {
            showError(error);
            dispatch(importAzureTeamsFailure(error?.response?.data?.message || 'Failed to import Azure teams'));
        }
    };

    // Azure team sync with intelligent IndexedDB caching - always call this, caching is handled internally
    const fetchAzureTeamSyncDifferences = async (orgId: string, userId: string): Promise<void> => {
        dispatch(fetchAzureSyncDifferencesStart());
        try {
            // First check if we have valid cached data (< 15 minutes old)
            const isCacheValid = await azureTeamSyncRepository.isCacheValid(orgId, userId);

            if (isCacheValid) {
                // Use cached data - no API call needed
                const cachedData = await azureTeamSyncRepository.getCachedData(orgId, userId);
                if (cachedData) {
                    dispatch(fetchAzureSyncDifferencesSuccess({ teams: cachedData }));
                    return;
                }
            }

            // Cache miss, expired, or invalid - fetch from API
            const response = await axios.get<AzureSyncTeamsResponse>(`/integration/sync_teams`);

            // Store the fresh response in IndexedDB for future use
            if (response.data && response.data.teams) {
                // Map API response to repository format (add isImported field to members if not present)
                const mappedTeams: AzureTeamSyncData[] = response.data.teams.map(team => ({
                    id: team.id,
                    name: team.name,
                    members: team.members.map(member => ({
                        id: member.id,
                        name: member.name,
                        email: member.email,
                        isImported: (member as any).isImported ?? false // Use existing value or default to false
                    }))
                }));

                await azureTeamSyncRepository.storeCachedData(orgId, userId, mappedTeams);
            }

            dispatch(fetchAzureSyncDifferencesSuccess(response.data));
        } catch (error: any) {
            handleAuthError(error, 'Failed to fetch sync differences', fetchAzureSyncDifferencesFailure);
        }
    };

    // Legacy version without caching (for backward compatibility)
    const fetchAzureTeamSyncDifferencesLegacy = async (orgId: string): Promise<void> => {
        dispatch(fetchAzureSyncDifferencesStart());
        try {
            const response = await axios.get<AzureSyncTeamsResponse>(`/integration/sync_teams`);
            dispatch(fetchAzureSyncDifferencesSuccess(response.data));
        } catch (error: any) {
            handleAuthError(error, 'Failed to fetch sync differences', fetchAzureSyncDifferencesFailure);
        }
    };

    // Add Azure team sync difference API without Redux (for direct use)
    const fetchAzureTeamSyncDifferencesRaw = async (): Promise<AzureSyncTeamsResponse> => {
        try {
            const response = await axios.get<AzureSyncTeamsResponse>(`/integration/sync_teams`);
            return response.data;
        } catch (error: any) {
            if (isAzureAuthError(error)) {
                toast.error("Please check your Azure DevOps credentials. Your token may have expired or is invalid.");
            } else {
                showError(error);
            }
            throw error;
        }
    };

    // Add Azure accept team members API
    const acceptAzureTeamMembers = async (orgId: string, teamId: number, members: AzureTeamSyncMember[]): Promise<void> => {
        dispatch(azureSyncActionsStart());
        try {
            const payload: AzureAcceptRejectMembersRequest = {
                team_id: teamId,
                members: members
            };

            const response = await axios.post<AzureAcceptRejectMembersResponse>(
                `/integration/accept_team_members`,
                payload
            );

            toast.success(response.data.message || 'Team members accepted successfully');
            dispatch(azureSyncActionsSuccess({ teamId, memberIds: members.map(m => m.id) }));
        } catch (error: any) {
            showError(error);
            dispatch(azureSyncActionsFailure(error?.response?.data?.message || 'Failed to accept team members'));
        }
    };

    // Add Azure reject team members API
    const rejectAzureTeamMembers = async (orgId: string, teamId: string, members: AzureTeamSyncMember[]): Promise<void> => {
        dispatch(azureSyncActionsStart());
        try {
            const payload = {
                team_id: teamId,
                members: members
            };

            const response = await axios.post<AzureAcceptRejectMembersResponse>(
                `/integration/reject_team_members`,
                payload
            );

            toast.success(response.data.message || 'Team members rejected successfully');
            dispatch(azureSyncActionsSuccess({ teamId: parseInt(teamId), memberIds: members.map(m => m.id) }));
        } catch (error: any) {
            showError(error);
            dispatch(azureSyncActionsFailure(error?.response?.data?.message || 'Failed to reject team members'));
        }
    };

    return {
        fetchAzureProjects,
        fetchAzureEpics,
        importAzureEpic,
        updateAzureStages,
        fetchAzureSettings,
        manageAzureProjects,
        refreshAzureStatusStages,
        fetchAzureUsers,
        importAzureUsers,
        fetchAzureTeams,
        fetchAzureTeamsSilently,
        importAzureTeams,
        fetchAzureTeamSyncDifferences,
        fetchAzureTeamSyncDifferencesLegacy,
        fetchAzureTeamSyncDifferencesRaw,
        acceptAzureTeamMembers,
        rejectAzureTeamMembers
    };
};

export default useAzureService;
